import { useRef, useCallback } from 'react';

/**
 * Custom hook for horizontal drag-to-scroll functionality
 * Works across all platforms (Mac/Windows) regardless of input device
 *
 * Supports multiple scroll methods:
 * - Click and drag: drag beyond 5px threshold to scroll
 * - Scroll wheel: vertical wheel scrolls horizontally (when content overflows)
 * - Shift + scroll wheel: explicit horizontal scroll
 *
 * @returns {Object} ref - Attach to the scrollable container (callback ref)
 *
 * Usage:
 * const { ref } = useDragScroll();
 * <div ref={ref} className="overflow-x-auto">...</div>
 */
export function useDragScroll() {
  const containerRef = useRef(null);
  const isMouseDown = useRef(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeftStart = useRef(0);
  const clickTarget = useRef(null);

  // Threshold in pixels before we consider it a drag (not a click)
  const DRAG_THRESHOLD = 5;

  const handleMouseDown = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;

    // Only trigger on primary mouse button
    if (e.button !== 0) return;

    isMouseDown.current = true;
    isDragging.current = false;
    startX.current = e.pageX;
    startY.current = e.pageY;
    scrollLeftStart.current = container.scrollLeft;
    clickTarget.current = e.target;
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isMouseDown.current) return;
    const container = containerRef.current;
    if (!container) return;

    const deltaX = Math.abs(e.pageX - startX.current);
    const deltaY = Math.abs(e.pageY - startY.current);

    // Check if we've moved beyond threshold to start dragging
    // Prefer horizontal movement (deltaX > deltaY) to avoid hijacking vertical scroll
    if (!isDragging.current && deltaX > DRAG_THRESHOLD && deltaX > deltaY) {
      isDragging.current = true;
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    }

    if (isDragging.current) {
      e.preventDefault();
      const walk = (e.pageX - startX.current) * 1.5; // Scroll speed multiplier
      container.scrollLeft = scrollLeftStart.current - walk;
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    const wasDragging = isDragging.current;
    isMouseDown.current = false;
    isDragging.current = false;
    clickTarget.current = null;

    const container = containerRef.current;
    if (container) {
      container.style.cursor = '';
      container.style.userSelect = '';
    }

    return wasDragging;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isMouseDown.current) {
      isMouseDown.current = false;
      isDragging.current = false;
      clickTarget.current = null;

      const container = containerRef.current;
      if (container) {
        container.style.cursor = '';
        container.style.userSelect = '';
      }
    }
  }, []);

  // Prevent click events on buttons when we were dragging
  const handleClick = useCallback((e) => {
    if (isDragging.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Handle scroll wheel - convert vertical scroll to horizontal
  const handleWheel = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;

    // Check if container has horizontal overflow
    const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
    if (!hasHorizontalOverflow) return;

    // Use deltaY for horizontal scroll (vertical wheel → horizontal scroll)
    // Also support shift+wheel for explicit horizontal scrolling
    const delta = e.shiftKey ? e.deltaX : e.deltaY;

    if (delta !== 0) {
      e.preventDefault();
      container.scrollLeft += delta;
    }
  }, []);

  // Callback ref - called when element mounts/unmounts
  const ref = useCallback((node) => {
    // Clean up old node's listeners
    if (containerRef.current) {
      containerRef.current.removeEventListener('mousedown', handleMouseDown);
      containerRef.current.removeEventListener('mousemove', handleMouseMove);
      containerRef.current.removeEventListener('mouseup', handleMouseUp);
      containerRef.current.removeEventListener('mouseleave', handleMouseLeave);
      containerRef.current.removeEventListener('click', handleClick, true);
      containerRef.current.removeEventListener('wheel', handleWheel);
    }

    // Store new node
    containerRef.current = node;

    // Add listeners to new node
    if (node) {
      node.addEventListener('mousedown', handleMouseDown);
      node.addEventListener('mousemove', handleMouseMove);
      node.addEventListener('mouseup', handleMouseUp);
      node.addEventListener('mouseleave', handleMouseLeave);
      node.addEventListener('click', handleClick, true);
      node.addEventListener('wheel', handleWheel, { passive: false });
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, handleClick, handleWheel]);

  return { ref };
}

export default useDragScroll;
